import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import styled from 'styled-components';
import { Button } from './Button';

const PaginationContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 2rem 0;
  
  @media (min-width: 768px) {
    gap: 0.75rem;
    margin: 3rem 0;
  }
`;

const PageButton = styled(Button)`
  min-width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  font-size: 0.9375rem;
  
  @media (min-width: 1024px) {
    min-width: 2.75rem;
    height: 2.75rem;
    font-size: 1rem;
  }
  
  ${props => props.$active && `
    background-color: ${props.theme.colors.primary};
    color: ${props.theme.colors.primaryForeground};
    pointer-events: none;
  `}
`;

const Ellipsis = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  color: ${props => props.theme.colors.mutedForeground};
  font-size: 0.9375rem;
  
  @media (min-width: 1024px) {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 1rem;
  }
`;

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  showFirstLast = true,
  maxVisible = 5 
}) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if we're near the start
      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis before if needed
      if (start > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      // Add ellipsis after if needed
      if (end < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <PaginationContainer aria-label="Pagination">
      {showFirstLast && (
        <PageButton
          variant="secondary"
          size="icon"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          aria-label="First page"
        >
          <ChevronsLeft />
        </PageButton>
      )}
      
      <PageButton
        variant="secondary"
        size="icon"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <ChevronLeft />
      </PageButton>

      {pageNumbers.map((page, index) => {
        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
          return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
        }
        
        return (
          <PageButton
            key={page}
            variant="secondary"
            $active={currentPage === page}
            onClick={() => onPageChange(page)}
            aria-label={`Page ${page}`}
            aria-current={currentPage === page ? 'page' : undefined}
          >
            {page}
          </PageButton>
        );
      })}

      <PageButton
        variant="secondary"
        size="icon"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <ChevronRight />
      </PageButton>

      {showFirstLast && (
        <PageButton
          variant="secondary"
          size="icon"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          aria-label="Last page"
        >
          <ChevronsRight />
        </PageButton>
      )}
    </PaginationContainer>
  );
};

export default Pagination;



